import {
  AlignmentType,
  BorderStyle,
  Footer,
  Header,
  HeadingLevel,
  Packer,
  PageNumber,
  Paragraph,
  TabStopType,
  TextRun,
  Document
} from 'docx';

export interface NoteEntry {
  text: string;
  createdBy: string;
  createdOn: string | Date;
}

export interface ClauseSectionItem {
  comparisonColor?: string;
  comparisonLabel?: string;
  text: string;
  notes: NoteEntry[];
}

export interface ParagraphSection {
  pageNumber: number | string;
  items: ClauseSectionItem[];
}

export interface BuildDocxOptions {
  headerText: string;
  footerText?: string;
  sections: ParagraphSection[];
}

const formatDate = (value: string | Date): string => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const NOTE_METADATA_FONT_SIZE = 18;
const NOTE_METADATA_RIGHT_TAB_POSITION = 9026;
const PAGE_HEADING_COLOR = '2F5496';

export async function buildNotesDocxBlob(
  options: BuildDocxOptions
): Promise<Blob> {
  const { headerText, footerText = '', sections } = options;

  const docSectionsChildren: Paragraph[] = [];

  sections.forEach((section, sectionIndex) => {
    const block: Paragraph[] = [];

    const headingParagraph = new Paragraph({
      heading: HeadingLevel.HEADING_2,
      keepLines: true,
      keepNext: true,
      spacing: {
        before: sectionIndex === 0 ? 0 : 240,
        after: 120
      },
      border: {
        bottom: {
          color: PAGE_HEADING_COLOR,
          space: 1,
          style: BorderStyle.SINGLE,
          size: 4
        }
      },
      children: [
        new TextRun({
          text: `Page ${section.pageNumber}`,
          bold: true,
          color: PAGE_HEADING_COLOR
        })
      ]
    });

    block.push(headingParagraph);

    section.items.forEach((item, itemIndex) => {
      const isLastItemInSection = itemIndex === section.items.length - 1;

      if (item.comparisonLabel?.trim()) {
        block.push(
          new Paragraph({
            keepLines: true,
            keepNext: true,
            spacing: {
              after: 40
            },
            children: [
              new TextRun({
                text: item.comparisonLabel,
                italics: true,
                color: item.comparisonColor
              })
            ]
          })
        );
      }

      block.push(
        new Paragraph({
          keepLines: true,
          keepNext: true,
          spacing: {
            after: 160
          },
          children: [
            new TextRun({
              text: 'Clause being reviewed: ',
              bold: true
            }),
            new TextRun({
              text: item.text,
              italics: true
            })
          ]
        })
      );

      if (item.notes.length === 0) {
        block.push(
          new Paragraph({
            keepLines: true,
            spacing: {
              after: isLastItemInSection ? 200 : 140
            },
            children: [
              new TextRun({
                text: 'Notes: None',
                italics: true
              })
            ]
          })
        );
      } else {
        item.notes.forEach((note, noteIndex) => {
          const isLastNote = noteIndex === item.notes.length - 1;
          const isLastParagraphForItem = isLastNote && isLastItemInSection;

          block.push(
            new Paragraph({
              keepLines: true,
              keepNext: !isLastNote,
              spacing: {
                after: 60
              },
              children: [
                new TextRun({ text: 'Note: ', bold: true }),
                new TextRun({ text: note.text })
              ]
            })
          );

          block.push(
            new Paragraph({
              keepLines: true,
              keepNext: !isLastNote,
              tabStops: [
                {
                  type: TabStopType.RIGHT,
                  position: NOTE_METADATA_RIGHT_TAB_POSITION
                }
              ],
              spacing: {
                after: isLastParagraphForItem ? 220 : 140
              },
              children: [
                new TextRun({
                  text: 'Created By: ',
                  bold: true,
                  size: NOTE_METADATA_FONT_SIZE
                }),
                new TextRun({
                  text: note.createdBy,
                  size: NOTE_METADATA_FONT_SIZE
                }),
                new TextRun({
                  text: '\t',
                  size: NOTE_METADATA_FONT_SIZE
                }),
                new TextRun({
                  text: 'Created On: ',
                  bold: true,
                  size: NOTE_METADATA_FONT_SIZE
                }),
                new TextRun({
                  text: formatDate(note.createdOn),
                  size: NOTE_METADATA_FONT_SIZE
                })
              ]
            })
          );
        });
      }

      if (!isLastItemInSection) {
        block.push(
          new Paragraph({
            spacing: {
              before: 80,
              after: 80
            },
            border: {
              bottom: {
                color: 'D1D5DB',
                space: 1,
                size: 6,
                style: BorderStyle.SINGLE
              }
            }
          })
        );
      }
    });

    if (sectionIndex < sections.length - 1) {
      block.push(
        new Paragraph({
          spacing: {
            before: 120,
            after: 120
          }
        })
      );
    }

    docSectionsChildren.push(...block);
  });

  const doc = new Document({
    sections: [
      {
        headers: {
          default: new Header({
            children: [
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  new TextRun({
                    text: headerText,
                    bold: true
                  })
                ]
              })
            ]
          })
        },
        footers: {
          default: new Footer({
            children: [
              ...(footerText
                ? [
                    new Paragraph({
                      alignment: AlignmentType.CENTER,
                      spacing: {
                        after: 40
                      },
                      children: [
                        new TextRun({
                          text: footerText,
                          size: 16
                        })
                      ]
                    })
                  ]
                : []),
              new Paragraph({
                alignment: AlignmentType.CENTER,
                children: [
                  new TextRun({
                    children: ['Page ', PageNumber.CURRENT]
                  })
                ]
              })
            ]
          })
        },
        children: docSectionsChildren
      }
    ]
  });

  return Packer.toBlob(doc);
}
